/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Params } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ColouriseService, LogService, NotifyService, UtilsService } from '../../../../../core/services';
import { ErrorResponseModel, NotificationModel } from '../../../../../core/models';
import { ViewConfigurationApiService } from '../../../shared';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-configurations',
    templateUrl: './configurations.component.html',
    styleUrls: ['./configurations.component.scss'],
})

export class ConfigurationComponent extends BaseComponent implements OnInit {
    /**
     * ID used as the title of the page.
     */
    public id: string;

    /**
     * Store the list of modules.
     */
    public modules: any[] = [];

    /**
     * Store the module id.
     */
    public moduleId: string;

    /**
     * Store the active tab.
     */
    public activeTab: string;

    /**
     * Store config data.
     */
    public configData: any;

    /**
     * The loading state of the page
     */
    public isLoading: boolean = true;

    /**
     * Store the page title.
     */
    public pageTitle: string = '';

    /**
     * Store all the previously loaded configs.
     * @private
     */
    private loadedConfigs: any[] = [];

    /**
     * Store module configs for getter/setter.
     * @private
     */
    private _moduleConfigs: any[] = [];

    constructor(
        private cdr: ChangeDetectorRef,
        private colouriseService: ColouriseService,
        private logService: LogService,
        private notifyService: NotifyService,
        private route: ActivatedRoute,
        private utilsService: UtilsService,
        private viewConfigApiService: ViewConfigurationApiService,
    ) {
        super();
    }

    set moduleConfigs(configs: any[]) {
        this._moduleConfigs = configs;
    }

    get moduleConfigs() {
        return this._moduleConfigs;
    }

    public ngOnInit(): void {
        this.getAllModules();
    }

    /**
     * Load config by id.
     * @param id
     */
    public loadConfigById(id: string): void {
        const configViewed: boolean = !!this.getViewedConfig(id);
        if (!this.loadedConfigs.length || !configViewed) {
            this.viewConfigApiService.getConfig(this.moduleId, id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        const response = {
                            type: 'file',
                            data: data.config
                        };
                        this.configData = this.colouriseService.displayDataInView(response);
                        this.addConfigToList(id);
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('ConfigurationComponent: loadConfigById',
                            'failed to load config data - ', error.message);
                    }
                });
        } else {
            this.configData = this.getViewedConfig(id)?.data;
        }
    }

    /**
     * If the config has already been viewed, get it.
     * @param id
     * @private
     */
    private getViewedConfig(id: string): any {
        return this.loadedConfigs.find(item => item.module === this.moduleId && item.configId === id);
    }

    /**
     * Add config to list for reference.
     * @param id
     * @private
     */
    private addConfigToList(id: string): void {
        this.loadedConfigs.push({
            module: this.moduleId,
            configId: id,
            data: this.configData
        });
    }

    /**
     * Set up the data for the page.
     * @private
     */
    private getAllModules(): void {
        this.viewConfigApiService.getConfigs()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any[]): void => {
                    this.modules = data;
                    this.setUpRouteSubscribers();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('ConfigurationComponent: getAllConfigs',
                        'Error getting data', error.message);
                }
            });
    }

    /**
     * Set up route subscribers.
     * @private
     */
    private setUpRouteSubscribers(): void {
        this.route.params
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (params: Params): void => {
                    this.isLoading = true;
                    this.resetClassVariables();
                    this.moduleId = params['service'];
                    const moduleName: any = this.modules.find((item: any): boolean => item.module === this.moduleId).moduleName;
                    this.pageTitle = moduleName ? this.utilsService.tryToMatchNameToLanguageFile(moduleName) : 'Configurations';
                    this.moduleConfigs = this.getConfigsByModuleId();
                    this.activeTab = this.moduleConfigs[0].config;
                    this.isLoading = false;
                }
            });
    }

    /**
     * Re-set class level variables.
     * @private
     */
    private resetClassVariables(): void {
        this.moduleConfigs = [];
        this.loadedConfigs = [];
        this.removeNotifications();
        this.cdr.detectChanges();
    }

    /**
     * Remove existing error notifications
     * @private
     */
    private removeNotifications(): void {
        const notifications: NotificationModel[] = this.notifyService.getCopyOfNotifications();
        const configNotification: NotificationModel = notifications.find((item: NotificationModel) => item.id.startsWith('stats-config'));
        if (configNotification) {
            this.notifyService.removeNotificationById(configNotification.id);
        }
    }

    /**
     * Get a list of the configs for the current module.
     * @private
     */
    private getConfigsByModuleId(): any {
        const selectedModule: any = this.modules.find((item: any): boolean => item.module === this.moduleId);
        if (!selectedModule.configs) {
            this.logService.error('ConfigurationComponent:getConfigsByModuleId', 'Unable to find module configs');
        } else {
            return this.formatConfigNames(selectedModule.configs);
        }
    }

    /**
     * Format config names.
     * @param configs
     * @private
     */
    private formatConfigNames(configs: any): any {
        for (const config of configs) {
            config.name = this.utilsService.tryToMatchNameToLanguageFile(config.name);
        }

        return configs;
    }
}
